/**
 * Lets a page opt the tours CTA into the global footer block.
 *
 * The CTA and the footer have to travel together: both belong at the bottom of
 * the window, and having the page render its own CTA meant two competing
 * sticky-footer mechanisms — the page reserving a viewport's height to push its
 * CTA down, and the layout doing the same for the footer. That left a 488px
 * hole between the content and the CTA.
 *
 * So the page no longer renders the CTA at all; it raises this flag and
 * Global/site-footer.vue renders the CTA directly above the footer markup, as
 * one block the layout pins to the bottom.
 */
export const useFooterCtaState = () => useState<boolean>('footer-tours-cta', () => false);

/**
 * Call from a page/template's setup to show the CTA above the footer.
 *
 * Takes an optional reactive condition for pages that only want the CTA some
 * of the time. The neighborhood page is the case that needed it: its map view
 * sizes itself to fill the viewport, so whatever follows is pinned to the
 * screen edge, and scrolling slid the red CTA up over the map and the site
 * list. Reported as the ad being stuck over the content (Asana O2-74).
 *
 * Clears itself on unmount so the flag can't leak into the next route on a
 * client-side navigation — `useState` is shared for the life of the app, and
 * without this every page after a site detail page would inherit the CTA.
 */
export const useFooterCta = (when?: MaybeRefOrGetter<boolean>) => {
    const show = useFooterCtaState();

    if (when === undefined) {
        show.value = true;
    } else {
        // watchEffect rather than a one-shot read: the condition is a view
        // toggle, so it changes long after setup has run.
        watchEffect(() => {
            show.value = toValue(when);
        });
    }

    onUnmounted(() => {
        show.value = false;
    });

    return show;
};
